import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { useTournamentAuth } from "../../utils/authorization";
import { useTournamentId, useTournamentSlug } from "../../context/TournamentRouteContext";
import { usePermissions } from "../../context/PermissionsContext";
import { useOrganization } from "../../context/OrganizationContext";
import { useAuth } from "../../context/AuthContext";
import { useOrganizationFeatures } from "../../context/OrganizationFeaturesContext";
import { notify } from "../UI/DesignStandards/Notifications/notify";
import { getRedirectBehavior, SignInLink } from "../../utility/routeUtils";
import {
  FaTrophy,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaSignOutAlt,
  FaSignInAlt,
} from "react-icons/fa";
import { useTournamentSteps } from "../../hooks/useTournamentSteps";
import {
  buildTournamentNavigation,
  getTournamentMobilePrimaryItems,
} from "../../utility/tournamentNav";
import {
  buildOrganizationNavigation,
  getOrganizationMobilePrimaryItems,
} from "../../utility/organizationNav";
import UpNextCard from "../Tournament/UpNextCard";
import TournamentBottomBar from "../Tournament/TournamentBottomBar";
import {
  TOURNAMENT_MOBILE_CLEARANCE_VAR,
  tournamentMobileClearance,
} from "../../utility/tournamentMobileChrome";
import {
  fetchTournamentSignupSettings,
  onlineSignupsEnabled,
  subscribeSignupSettingsChanged,
} from "../../utility/signupSettings";
import {
  tournamentHasBracketProducts,
  subscribeBracketProducts,
} from "../../utility/brackets/api";
import { getSignupAttentionCount } from "../../utility/signupRpc";
import { useDueSignupCount } from "../../hooks/useDueSignupCount";

const UnifiedSidebar = ({
  context = "tournament", // "tournament" or "organization"
  isCollapsed = false,
  onToggleCollapse = () => {},
  className = "",
  onClose,
}) => {
  const location = useLocation();
  const tournamentId = useTournamentId();
  const tournamentSlug = useTournamentSlug();
  const [tournamentName, setTournamentName] = useState("");
  const [isLoadingTournament, setIsLoadingTournament] = useState(false);
  const [showTooltip, setShowTooltip] = useState(null);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const sidebarRef = useRef(null);
  const { currentOrganization } = useOrganization();
  const { canManage } = useTournamentAuth(tournamentId);
  const { user, signOut } = useAuth();
  const dueCount = useDueSignupCount(tournamentId, {
    enabled: context === "tournament",
  });
  const { getNextStep } = useTournamentSteps();
  const nextStep = canManage ? getNextStep() : null;
  const navigate = useNavigate();

  const permissions = usePermissions();
  const { hasFeature, ready: featuresReady } = useOrganizationFeatures();
  const [signupSettings, setSignupSettings] = useState(null);
  const [attentionCount, setAttentionCount] = useState(0);
  const [hasBrackets, setHasBrackets] = useState(false);

  // Fetch tournament name and signup settings when component mounts
  useEffect(() => {
    const fetchTournamentData = async () => {
      if (context === "tournament" && tournamentId) {
        setIsLoadingTournament(true);
        try {
          // Fetch tournament name
          const { data: tournamentData, error: tournamentError } =
            await supabase
              .from("tournaments")
              .select("name")
              .eq("id", tournamentId)
              .single();

          if (tournamentError) {
            console.error("Error fetching tournament name:", tournamentError);
            setTournamentName("Tournament");
          } else {
            setTournamentName(tournamentData.name);
          }

          // Fetch signup settings
          const tournamentSettings =
            await fetchTournamentSignupSettings(tournamentId);
          setSignupSettings(tournamentSettings);

          try {
            setHasBrackets(await tournamentHasBracketProducts(tournamentId));
          } catch {
            setHasBrackets(false);
          }

          if (onlineSignupsEnabled(tournamentSettings) && canManage) {
            try {
              const countData = await getSignupAttentionCount({
                tournamentId,
              });
              if (countData?.needsAttentionCount !== undefined) {
                setAttentionCount(countData.needsAttentionCount);
              }
            } catch (error) {
              console.error("Error fetching signup attention count:", error);
            }
          }
        } catch (error) {
          console.error("Error in sidebar:", error);
          setTournamentName("Tournament");
        } finally {
          setIsLoadingTournament(false);
        }
      }
    };

    fetchTournamentData();
  }, [context, tournamentId, canManage, location.pathname]);

  useEffect(() => {
    if (context !== "tournament" || !tournamentId) return undefined;

    return subscribeSignupSettingsChanged((id, settings) => {
      if (id !== tournamentId) return;
      setSignupSettings(settings);
      if (!onlineSignupsEnabled(settings)) {
        setAttentionCount(0);
        return;
      }
      if (!canManage) return;
      getSignupAttentionCount({ tournamentId })
        .then((countData) => {
          if (countData?.needsAttentionCount === undefined) return;
          setAttentionCount(countData.needsAttentionCount);
        })
        .catch((error) => {
          console.error("Error fetching signup attention count:", error);
        });
    });
  }, [context, tournamentId, canManage]);

  useEffect(() => {
    if (context !== "tournament" || !tournamentId) return undefined;
    const refresh = () => {
      tournamentHasBracketProducts(tournamentId)
        .then(setHasBrackets)
        .catch(() => setHasBrackets(false));
    };
    refresh();
    return subscribeBracketProducts(tournamentId, refresh);
  }, [context, tournamentId]);

  // Close mobile sidebar when route changes
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Handle escape key to close mobile sidebar
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isMobileOpen) {
        setIsMobileOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isMobileOpen]);

  const signupsOn =
    signupSettings != null && onlineSignupsEnabled(signupSettings);

  const tournamentNavigation = buildTournamentNavigation({
    tournamentSlug,
    location,
    signupsOn,
    dueCount,
    hasBrackets,
    attentionCount,
  });

  const organizationNavigation = buildOrganizationNavigation();

  const navigation =
    context === "tournament" ? tournamentNavigation : organizationNavigation;

  // Filter navigation items based on permissions
  const filteredNavigation = navigation.filter((item) => {
    // If it's an admin-only item, check if user can manage
    if (item.adminOnly) {
      return canManage;
    }
    // If it requires organization permission, check permissions
    if (item.requiredPermission) {
      const hasPermission = permissions?.[item.requiredPermission] === true;
      if (!hasPermission) return false;
    }
    if (item.requiredFeature) {
      if (!featuresReady) return false;
      return hasFeature(item.requiredFeature);
    }
    // Show all non-admin items
    return true;
  });

  const mobilePrimaryItems =
    context === "tournament"
      ? getTournamentMobilePrimaryItems(filteredNavigation, {
          canManage,
          signupsOn,
        })
      : getOrganizationMobilePrimaryItems(filteredNavigation);
  const showUpNextCard = context === "tournament" && Boolean(nextStep);

  useEffect(() => {
    document.documentElement.style.setProperty(
      TOURNAMENT_MOBILE_CLEARANCE_VAR,
      tournamentMobileClearance()
    );
    return () => {
      document.documentElement.style.removeProperty(
        TOURNAMENT_MOBILE_CLEARANCE_VAR
      );
    };
  }, []);

  const handleNavClick = () => {
    // Close sidebar on mobile when navigation item is clicked
    if (window.innerWidth < 1024) {
      setIsMobileOpen(false);
    }
  };

  const handleSignOutClick = async () => {
    try {
      await signOut();
      notify.success("Successfully signed out");

      // Simple redirect logic - let ProtectedRoute handle route protection
      const currentPath = window.location.pathname;
      const redirectBehavior = getRedirectBehavior(currentPath);
      navigate(redirectBehavior.destination);
    } catch (error) {
      notify.error("Error signing out. Please try again.");
    }
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-brand-charcoal/15">
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          <div className="w-8 h-8 bg-brand-gold rounded-lg flex items-center justify-center flex-shrink-0">
            <FaTrophy className="text-brand-charcoal text-sm" />
          </div>
          {!isCollapsed && (
            <div className="min-w-0 flex-1 overflow-hidden">
              <p
                className="text-sm font-display font-bold text-brand-charcoal truncate"
                title={
                  context === "tournament"
                    ? tournamentName
                    : currentOrganization?.name
                }
              >
                {context === "tournament"
                  ? isLoadingTournament
                    ? "Loading..."
                    : tournamentName || "Tournament"
                  : currentOrganization?.name || "Organization"}
              </p>
              <p className="text-xs text-brand-muted truncate">
                {context === "tournament"
                  ? "Tournament Management"
                  : "Organization Management"}
              </p>
            </div>
          )}
        </div>

        {/* Collapse/Expand Button (Desktop) */}
        <button
          onClick={onToggleCollapse}
          className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg hover:bg-brand-charcoal/5 transition-colors duration-200"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <FaChevronRight className="w-4 h-4 text-brand-muted" />
          ) : (
            <FaChevronLeft className="w-4 h-4 text-brand-muted" />
          )}
        </button>

        {/* Close Button (Mobile) */}
        <button
          onClick={() => setIsMobileOpen(false)}
          className="lg:hidden flex items-center justify-center min-h-11 min-w-11 rounded-lg hover:bg-brand-charcoal/5 transition-colors duration-200"
          aria-label="Close sidebar"
        >
          <FaTimes className="w-4 h-4 text-brand-muted" />
        </button>
      </div>

      {showUpNextCard ? (
        <>
          <div className={`${isCollapsed ? "p-2" : "px-3 pt-3"} hidden lg:block`}>
            <UpNextCard nextStep={nextStep} compact={isCollapsed} />
          </div>
          <div className="px-3 pt-3 lg:hidden">
            <UpNextCard
              nextStep={nextStep}
              variant="menu"
              onNavigate={handleNavClick}
            />
          </div>
        </>
      ) : null}

      {/* Navigation */}
      <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1">
        {filteredNavigation.map((item, index) => {
          const Icon = item.icon;
          const previous = filteredNavigation[index - 1];
          const startManage =
            context === "tournament" && item.adminOnly && !previous?.adminOnly;

          return (
            <div key={item.name} className="relative group">
              {startManage && !isCollapsed ? (
                <div className="pt-3 mt-1 mb-1 px-3 text-xs font-display font-bold tracking-wider uppercase text-brand-muted">
                  Manage
                </div>
              ) : null}
              {startManage && isCollapsed ? (
                <div className="border-t border-brand-charcoal/15 my-2" />
              ) : null}
              <NavLink
                to={item.href}
                className={({ isActive }) => {
                  const active = item.matchPrefix
                    ? location.pathname.startsWith(item.matchPrefix)
                    : isActive;
                  return `flex items-center px-3 py-2.5 max-lg:min-h-11 text-sm font-medium rounded-lg transition-all duration-200 ${
                    active
                      ? "bg-brand-gold/20 text-brand-charcoal border-r-2 border-brand-gold-ink"
                      : "text-brand-muted hover:bg-brand-charcoal/5 hover:text-brand-charcoal hover:translate-x-1"
                  } ${item.comingSoon ? "opacity-50 cursor-not-allowed" : ""} ${
                    isCollapsed ? "justify-center" : ""
                  }`;
                }}
                title={!isCollapsed ? item.description : item.name}
                onClick={(e) => {
                  if (item.comingSoon) {
                    e.preventDefault();
                  } else {
                    handleNavClick();
                  }
                }}
                onMouseEnter={() => isCollapsed && setShowTooltip(item.name)}
                onMouseLeave={() => isCollapsed && setShowTooltip(null)}
                end={item.isExact}
              >
                <Icon
                  className={`flex-shrink-0 w-5 h-5 transition-all duration-200 ${
                    isCollapsed ? "mx-auto" : "mr-3"
                  }`}
                  aria-hidden="true"
                />
                {!isCollapsed && (
                  <span className="truncate transition-all duration-200 flex items-center justify-between w-full">
                    <span>
                      {item.name}
                      {item.comingSoon && (
                        <span className="ml-2 text-xs text-brand-mist">
                          (Soon)
                        </span>
                      )}
                    </span>
                    {item.badge && (
                      <span
                        className={`ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full min-w-[1.25rem] ${
                          item.badgeClass || "text-white bg-red-500"
                        }`}
                      >
                        {item.badge}
                      </span>
                    )}
                  </span>
                )}
                {isCollapsed && item.badge && (
                  <span
                    className={`absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none rounded-full min-w-[1.25rem] ${
                      item.badgeClass || "text-white bg-red-500"
                    }`}
                  >
                    {item.badge}
                  </span>
                )}
              </NavLink>

              {/* Tooltip for collapsed state */}
              {isCollapsed && showTooltip === item.name && (
                <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-md shadow-lg z-50 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {item.name}
                  <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      {!isCollapsed && (
        <div className="p-4 border-t border-brand-charcoal/15">
          <div className="text-xs text-brand-muted text-center">
            {context === "tournament"
              ? "Tournament Management"
              : "Organization Management"}
          </div>
          <div className="mt-2 flex justify-center space-x-2">
            {user ? (
              <button
                type="button"
                onClick={handleSignOutClick}
                className="flex items-center px-3 py-2 max-lg:min-h-11 text-sm font-medium text-brand-charcoal rounded-lg hover:bg-brand-charcoal/5 focus:outline-none focus:ring-2 focus:ring-brand-gold-ink transition-colors duration-200"
                aria-label="Sign out"
              >
                <FaSignOutAlt className="w-4 h-4 mr-1" />
                Sign Out
              </button>
            ) : (
              <SignInLink
                to="/signin"
                className="flex items-center px-3 py-2 max-lg:min-h-11 text-sm font-medium text-brand-charcoal rounded-lg hover:bg-brand-charcoal/5 focus:outline-none focus:ring-2 focus:ring-brand-gold-ink transition-colors duration-200"
              >
                <FaSignInAlt className="w-4 h-4 mr-1" />
                Sign in
              </SignInLink>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div
        ref={sidebarRef}
        className={`hidden lg:flex lg:sticky lg:top-0 lg:self-start lg:h-screen lg:flex-shrink-0 flex-col bg-white border-r border-brand-charcoal/10 shadow-sm transition-all duration-300 overflow-hidden ${
          isCollapsed ? "w-16" : "w-64"
        } ${className}`}
      >
        <SidebarContent />
      </div>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-white border-r border-brand-charcoal/10 shadow-xl z-50 transform transition-transform duration-300 lg:hidden flex flex-col min-h-0 ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <SidebarContent />
      </div>

      <TournamentBottomBar
        items={mobilePrimaryItems}
        pathname={location.pathname}
        onOpenMenu={() => setIsMobileOpen(true)}
        onNavClick={handleNavClick}
        hasNextAction={showUpNextCard}
        label={context === "tournament" ? "Tournament" : "Organization"}
      />
    </>
  );
};

export default UnifiedSidebar;
